/** Inicio de sesión (SDD §7.2): Google en un clic o enlace mágico al correo. */
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useApp, errorText } from '../components/AppContext'
import { Brand } from '../components/ui'
import { auth, safeNext } from '../lib/auth'
import { DEV_MODE } from '../lib/config'
import { useDocumentTitle } from '../lib/hooks'
import { marcarTerminosAceptados } from '../lib/terminos'
import { SITIO_URL, TURNSTILE_SITE_KEY, renderTurnstile } from '../lib/thirdparty'

export function Login() {
  useDocumentTitle('Entrar')
  const { session, ready } = useApp()
  const [params] = useSearchParams()
  const next = safeNext(params.get('next'))
  const nav = useNavigate()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Captcha del registro por correo (solo si está configurado): frena abuso del plan gratis
  const captchaEl = useRef<HTMLDivElement>(null)
  const [captcha, setCaptcha] = useState<string | null>(null)
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !captchaEl.current || sent) return
    let cleanup: (() => void) | undefined
    renderTurnstile(captchaEl.current, setCaptcha)
      .then((c) => (cleanup = c))
      .catch(() => setError('No se pudo cargar la verificación. Recarga la página.'))
    return () => cleanup?.()
  }, [sent])

  useEffect(() => {
    if (ready && session) nav(next, { replace: true })
  }, [ready, session, next, nav])

  async function google() {
    setBusy(true)
    setError(null)
    marcarTerminosAceptados()
    try {
      await auth.signInWithGoogle(next)
    } catch (e) {
      setError(errorText(e))
      setBusy(false)
    }
  }

  async function magic(e: React.FormEvent) {
    e.preventDefault()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError('Escribe un correo válido.')
    if (TURNSTILE_SITE_KEY && !captcha) return setError('Completa la verificación de abajo.')
    setBusy(true)
    setError(null)
    marcarTerminosAceptados()
    try {
      await auth.signInWithEmail(email.trim(), next, captcha ?? undefined)
      setSent(true)
    } catch (err) {
      // Supabase limita el envío: mensaje genérico, sin revelar si la cuenta existe
      setError(
        /rate|limit|seconds/i.test(errorText(err))
          ? 'Espera un minuto antes de pedir otro enlace.'
          : errorText(err)
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth">
      <div className="auth-form">
        <div className="auth-top">
          <Brand to="/login" height={26} />
          <Link to="/seguridad" className="faint" style={{ fontSize: 13 }}>
            Seguridad y privacidad
          </Link>
        </div>
        <div className="auth-inner stack" style={{ gap: 16 }}>
          <div>
            <h1>Entra a eco</h1>
            <p className="muted" style={{ marginTop: 8 }}>
              Notas de tus reuniones en español, sin bots en la llamada.
            </p>
          </div>
          {DEV_MODE && (
            <div className="notice warn">Modo desarrollo: entras como dev@eco.local.</div>
          )}
          {sent ? (
            <div className="notice good" role="status">
              Te enviamos un enlace a <strong>{email}</strong>. Ábrelo en este mismo navegador. Si
              no llega en un par de minutos, revisa la carpeta de spam.
            </div>
          ) : (
            <>
              <button className="btn block google" onClick={() => void google()} disabled={busy}>
                <GoogleG /> Continuar con Google
              </button>
              <div className="divider">o con tu correo</div>
              <form className="stack" onSubmit={(e) => void magic(e)} noValidate>
                <label className="field">
                  <span>Correo</span>
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@empresa.cl"
                    required
                  />
                </label>
                {TURNSTILE_SITE_KEY && <div ref={captchaEl} style={{ minHeight: 65 }} />}
                <button className="btn primary block" disabled={busy}>
                  Enviarme un enlace
                </button>
              </form>
            </>
          )}
          {error && (
            <div className="notice error" role="alert">
              {error}
            </div>
          )}
          <p className="faint" style={{ fontSize: 12 }}>
            Al continuar aceptas los{' '}
            <a href={`${SITIO_URL}/terminos/`} target="_blank" rel="noreferrer">
              Términos
            </a>{' '}
            y la{' '}
            <a href={`${SITIO_URL}/privacidad/`} target="_blank" rel="noreferrer">
              Política de privacidad
            </a>
            . Tu audio se borra de nuestros servidores apenas termina la transcripción.
          </p>
        </div>
      </div>
      <aside className="auth-aside" aria-hidden="true">
        <div className="sample">
          <div className="sample-head">
            <span className="serif" style={{ fontSize: 19 }}>
              Planificación trimestral
            </span>
            <span className="faint" style={{ fontSize: 12.5 }}>
              Hoy · 42 min · Google Meet
            </span>
          </div>
          <div className="sample-sec">Resumen</div>
          <p>
            Se priorizó el lanzamiento en regiones y se acordó un tope de cinco millones para la
            campaña. Queda pendiente definir si se suma radio.
          </p>
          <div className="sample-sec">Tareas</div>
          <div className="task">
            <span className="box" />
            <span>
              Pedir las piezas a la agencia <span className="who">— Carla · viernes</span>
              <span className="ev">12:40</span>
            </span>
          </div>
          <div className="task">
            <span className="box" />
            <span>
              Enviar el presupuesto detallado <span className="who">— Diego · miércoles</span>
              <span className="ev">18:05</span>
            </span>
          </div>
        </div>
        <p className="auth-quote">
          Grabas desde el navegador, sin que un bot entre a la llamada. El audio se borra apenas
          termina la transcripción.
        </p>
      </aside>
    </div>
  )
}

/** Logo de Google para su botón (según sus normas de marca: la «G» a color). */
function GoogleG() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.9 6.1C12.5 13.6 17.8 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.7 6c4.5-4.2 6.9-10.3 6.9-17.7z"
      />
      <path
        fill="#FBBC05"
        d="M10.6 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.6 0 20.2 0 24s1 7.4 2.7 10.7l7.9-6.1z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.7-6c-2.1 1.4-4.9 2.3-8.2 2.3-6.2 0-11.5-4.1-13.4-9.9l-7.9 6.1C6.6 42.6 14.6 48 24 48z"
      />
    </svg>
  )
}
